import moment from 'moment';
import { Box, Panel } from '@bigcommerce/big-design';
import Table from '../../../../components/Table';
import CopyableText from '../../../../components/CopyableText';
import OrderStatusColumn from '../../OrdersList/components/OrderStatusColumn';
import OrderTagColumn from '../../OrdersList/components/OrderTagColumn';
import { NewTabButton, ViewOrderButton, CopyOrderNumberButton } from '../../../../utils/OrderButtons';
import { useOrderStatusMap } from '../../../../context/OrderStatuses';

export default function PreviousOrders({ orders }) {
	const statusMap = useOrderStatusMap();
	const items = (orders || []).map((o) => {
		// normalize tags to array
		let normalizedTags = [];
		if (Array.isArray(o.tags)) {
			normalizedTags = o.tags;
		} else if (typeof o.tags === 'string') {
			const trimmed = o.tags.replace(/^\{|\}$/g, '');
			normalizedTags = trimmed.length ? trimmed.split(',').map((t) => t.trim()) : [];
		}

		// normalize photos to boolean
		let hasPhotos = false;
		if (Array.isArray(o.photos)) {
			hasPhotos = o.photos.length > 0;
		} else if (typeof o.photos === 'string') {
			const trimmed = o.photos.replace(/^\{|\}$/g, '');
			hasPhotos = trimmed.length > 0;
		} else {
			hasPhotos = Boolean(o.photos);
		}

		// normalize status: prefer bc_status_id when numeric, else keep text
		let normalizedStatus = null;
		const bcStatusAsNumber = Number(o.bc_status_id);
		if (Number.isFinite(bcStatusAsNumber)) {
			normalizedStatus = bcStatusAsNumber;
		} else if (o.status) {
			normalizedStatus = o.status; // text; we will map to id at render time
		}

		// normalize total to numeric string, preserve original if unusable
		let normalizedTotal = o.total;
		if (normalizedTotal != null) {
			const numeric = parseFloat(String(normalizedTotal).replace(/[^0-9.-]/g, ''));
			normalizedTotal = Number.isFinite(numeric) ? numeric : null;
		}

		return {
			bc_order_id: o.bc_order_id,
			customer_name: o.customer_name || o.customerName || 'NA',
			date: o.date || o.created || null,
			estimatedShippingDate: o.estimatedShippingDate || o.estimatedshippingdate || 'NA',
			vehicles: Array.isArray(o.vehicles) ? o.vehicles : (o.vehicle || 'NA'),
			total: normalizedTotal,
			vendor: o.vendor || 'NA',
			product: o.product || (Array.isArray(o.products) ? o.products.filter(Boolean).join(', ') : 'NA'),
			color: o.color || (Array.isArray(o.colors) ? o.colors.filter(Boolean).join(', ') : 'NA'),
			status: normalizedStatus ?? (Number.isFinite(Number(o.bc_status_id)) ? Number(o.bc_status_id) : null),
			photos: hasPhotos,
			tags: normalizedTags,
		};
	});
	const columns = [
		{
			hash: 'view_order',
			width: '40px',
			header: 'View',
			render: ({ bc_order_id }) => (
				<ViewOrderButton orderId={bc_order_id} />
			),
		},
		{
			hash: 'new_tab',
			width: '40px',
			header: 'Open',
			render: ({ bc_order_id }) => (
				<NewTabButton orderId={bc_order_id} />
			),
		},
		{
			header: 'Order',
			hash: 'bc_order_id',
			render: ({ bc_order_id }) => (
				<CopyOrderNumberButton orderId={bc_order_id} />
			),
		},
		{
			header: 'Customer',
			hash: 'customer_name',
			render: ({ customer_name }) => <CopyableText text={customer_name} />,
		},
		{
			header: 'Order Date',
			hash: 'date',
			render: ({ date }) => <CopyableText text={date ? moment(date).format('M/DD/YYYY') : 'NA'} />,
		},
		{
			header: 'Est. Ship Date',
			hash: 'estimatedShippingDate',
			render: ({ estimatedShippingDate }) => <CopyableText text={estimatedShippingDate} />,
		},
		{
			header: 'Vehicles',
			hash: 'vehicles',
			render: ({ vehicles }) => {
				if (Array.isArray(vehicles)) {
					return <CopyableText text={vehicles.filter(v => v && v.trim()).join(', ')} />;
				}
				return <CopyableText text={vehicles || 'NA'} />;
			},
		},
		{
			header: 'Product',
			hash: 'product',
			render: ({ product }) => (
				<CopyableText text={product || 'NA'} />
			),
		},
		{
			header: 'Color',
			hash: 'color',
			render: ({ color }) => (
				<CopyableText text={color || 'NA'} />
			),
		},
		{
			header: 'Total',
			hash: 'total',
			render: ({ total }) => (Number.isFinite(total) ? `$${total.toFixed(2)}` : 'NA'),
		},
		{
			header: 'Vendor',
			hash: 'vendor',
			render: ({ vendor }) => vendor,
		},
		{
			header: 'Status',
			hash: 'status',
			render: ({ status }) => {
				if (typeof status === 'number') return <OrderStatusColumn status={status} />;
				if (status && statusMap && statusMap.size) {
					let matchedId = null;
					for (const [id, name] of statusMap.entries()) {
						if (name === status) { matchedId = id; break; }
					}
					if (matchedId !== null) return <OrderStatusColumn status={matchedId} />;
				}
				return <CopyableText text={status || 'NA'} />;
			},
		},
		{
			header: 'Photos',
			hash: 'photos',
			render: ({ photos }) => (photos ? 'Yes' : 'No'),
		},
		{
			header: 'Tags',
			hash: 'tags',
			render: ({ tags }) => <OrderTagColumn tags={tags || []} />,
			isSortable: true,
		},
	];

	if (orders?.length <= 0)
		return null;

	return (
		<Panel header="Previous Orders" marginTop="Large" id="previousOrders">
			<Box marginTop="Large">
				<Table columns={columns} items={items} />
			</Box>
		</Panel>
	);
}
